import {
  DocumentModel,
  ApprovalModel,
  ApprovalsModel,
  AttachmentModel,
} from '../models/Documents.model';
import { FeedItemDTO } from '../../feedItems/dtos/FeedItem.dto';
import {
  AttachmentDTO,
  ApprovalDTO,
  ApprovalsDTO,
  DocumentFeedItemType,
  DocumentRequestDTO,
  ApprovalRequestDTO,
  AttachmentRequestDTO,
  CreateDocumentRequestDTO,
  FormDocumentRequestDTO,
} from '../infra/types';
import { TagUtil } from '../../tags/utils/TagUtil';
import { generateUID } from '../../../../shared/components/toast/utils';

export class DocumentUtil {
  public static mapToViewModel(
    dto: FeedItemDTO<DocumentFeedItemType>,
  ): DocumentModel {
    return {
      id: dto.id,
      headline: dto.headline,
      tagline: dto.tagline,
      coverImage: dto.coverImage,
      attachment: AttachmentUtil.maptToViewModel(dto.feedItem.attachment),
      approval:
        dto.feedItem.approval &&
        ApprovalsUtil.maptToViewModel(dto.feedItem.approval),
      status: dto.status,
      access: dto.access,
      tags: dto.tags ? dto.tags.map((tag) => TagUtil.mapToViewModel(tag)) : [],
      publishedOn: dto.publishedOn,
      citationRequired: dto.citationRequired,
      recipients: dto.recipients,
      feedType: 'DocumentItem',
    };
  }

  public static toRequestDTO(model?: DocumentModel): DocumentRequestDTO {
    return {
      id: model ? model.id : undefined,
      headline: model ? model.headline : '',
      tagline: model ? model.tagline : undefined,
      tags: model ? model.tags : [],
      coverImage: model ? model.coverImage : undefined,
      citationRequired: model ? model.citationRequired : false,
      status: model ? model.status : 'DRAFT',
      attachment: model
        ? AttachmentUtil.toRequestDTO(model.attachment)
        : AttachmentUtil.toRequestDTO(),
      approval: model
        ? model.approval
          ? ApprovalsUtil.toRequestDTO(model.approval)
          : undefined
        : undefined,
    };
  }

  public static toFormRequestDTO(
    model?: DocumentModel,
  ): FormDocumentRequestDTO {
    return {
      id: model ? model.id : undefined,
      headline: model ? model.headline : '',
      tagline: model ? model.tagline : '',
      tags: model ? model.tags : [],
      coverImage: model ? model.coverImage : undefined,
      citationRequired: model ? model.citationRequired : false,
      status: model ? model.status : 'DRAFT',
      attachment: model
        ? AttachmentUtil.toRequestDTO(model.attachment)
        : AttachmentUtil.toRequestDTO(),
      approval: model
        ? model.approval
          ? ApprovalsUtil.toRequestDTO(model.approval)
          : undefined
        : undefined,
      withApproval:
        model && model.approval && model.approval.emails ? true : false,
    };
  }

  public static formToRequestDTO(
    formRequest: FormDocumentRequestDTO,
  ): DocumentRequestDTO {
    return {
      id: formRequest.id,
      headline: formRequest.headline,
      tagline: formRequest.tagline,
      tags: formRequest.tags,
      coverImage: formRequest.coverImage,
      citationRequired: formRequest.citationRequired,
      status: formRequest.status,
      attachment: formRequest.attachment,
      approval: formRequest.approval,
    };
  }

  public static toCreateRequestDTO(
    requestDTO: DocumentRequestDTO,
  ): CreateDocumentRequestDTO {
    return {
      id: requestDTO.id,
      headline: requestDTO.headline,
      tagline: requestDTO.tagline,
      coverImage: requestDTO.coverImage,
      tags: requestDTO.tags,
      approval: requestDTO.approval,
      citationRequired: requestDTO.citationRequired,
      status: requestDTO.status,
      attachment:
        typeof requestDTO.attachment !== 'string'
          ? requestDTO.attachment.id
          : requestDTO.attachment,
    };
  }
}

export class AttachmentUtil {
  public static maptToViewModel(dto: AttachmentDTO): AttachmentModel {
    return {
      id: dto.id,
      filePath: dto.filePath,
      mimeType: dto.mimeType,
      fileSize: dto.fileSize,
      originalName: dto.originalName,
      fileName: dto.fileName,
      fileUrl: dto.fileUrl,
      displayName: dto.displayName,
    };
  }

  public static toRequestDTO(model?: AttachmentModel): AttachmentRequestDTO {
    return {
      id: model ? model.id : generateUID(),
      filePath: model ? model.filePath : '',
      fileName: model ? model.fileName : '',
      fileSize: model ? model.fileSize : 0,
      mimeType: model ? model.mimeType : '',
      originalName: model ? model.originalName : '',
      fileUrl: model ? model.fileUrl : '',
      displayName: model ? model.displayName : undefined,
    };
  }
}

export class ApprovalsUtil {
  public static maptToViewModel(dto: ApprovalsDTO): ApprovalsModel {
    return {
      emails: dto.emails,
      approvals: dto.approvals
        ? dto.approvals.map((approval) => ApprovalUtil.mapToViewModel(approval))
        : [],
    };
  }

  public static toRequestDTO(model?: ApprovalsModel): ApprovalRequestDTO {
    return {
      emails: model ? model.emails : undefined,
    };
  }
}

export class ApprovalUtil {
  public static mapToViewModel(dto: ApprovalDTO): ApprovalModel {
    return {
      id: dto.id,
      user: dto.user,
      status: dto.status,
      comments: dto.comments,
    };
  }
}
